import { PermissionAccessProvider, PermissionAwareTextarea } from '../../../contexts/PermissionAccessContext';
import type { UnitDetail } from '../../../types/safetySystem';
import { PREFERENCE_OPTIONS, STATUS_OPTIONS, TURNOVER_OPTIONS, lookupOptions } from './unitForm';
import { DateField, Field, HiddenField, LookupSelectField, MaskedSsnField, Section, SelectField, TextField, YesNoField, inputClass, str } from './unitFields';

/**
 * Driver tab of the Unit drawer — three columns mirroring the V1 Safety driver page.
 * Col 1: identity / address / contact. Col 2: assignment, GWI Move, device, alerts, fuel card.
 * Col 3: photo, status widget, contract/start dates, referral, GWI Move 2.
 *
 * All inputs sit inside a PermissionAccessProvider at level 2 (edit) or 1 (view), so the
 * shared pickers and the PermissionAware* primitives disable themselves below edit level.
 */

export interface UnitDriverTabProps {
  detail: UnitDetail;
  driver: Record<string, any>;
  unit: Record<string, any>;
  onDriverChange: (key: string, value: unknown) => void;
  onUnitChange: (key: string, value: unknown) => void;
  canEdit: boolean;
  /** `safety-system-payroll-sensitive` >= 2 — shows the SSN Edit control and the Date of Birth
   *  (the server returns DOB as null below level 2, see safety_system/pii.py). */
  canSensitive: boolean;
  ssn: string | null;
  onSsnChange: (value: string | null) => void;
  photoUrl: string | null;
  portalContainer: HTMLElement | null;
}

export default function UnitDriverTab({
  detail, driver, unit, onDriverChange, onUnitChange, canEdit, canSensitive, ssn, onSsnChange, photoUrl, portalContainer,
}: UnitDriverTabProps) {
  const L = detail.lookups;
  const d = (key: string) => (v: unknown) => onDriverChange(key, v);
  const u = (key: string) => (v: unknown) => onUnitChange(key, v);
  const status = str(unit.status).toUpperCase();

  return (
    <PermissionAccessProvider accessLevel={canEdit ? 2 : 1}>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        {/* ---- Column 1: identity / address / contact ---- */}
        <div className="space-y-3">
          <Section title="Driver">
            <MaskedSsnField label="SSN" last4={driver.ssn_last4} value={ssn} onChange={onSsnChange} canEdit={canSensitive && canEdit} className="col-span-2" />
            <TextField label="First Name" value={driver.firstname} onChange={d('firstname')} />
            <TextField label="Middle Name" value={driver.middlename} onChange={d('middlename')} />
            <TextField label="Last Name" value={driver.lastname} onChange={d('lastname')} className="col-span-2" />
            <TextField label="Address 1" value={driver.add1} onChange={d('add1')} className="col-span-2" />
            <TextField label="Address 2" value={driver.add2} onChange={d('add2')} className="col-span-2" />
            <TextField label="City" value={driver.city} onChange={d('city')} className="col-span-2" />
            <TextField label="State" value={driver.state} onChange={d('state')} maxLength={2} />
            <TextField label="Zip" value={driver.zip} onChange={d('zip')} />
            {canSensitive
              ? <DateField label="Date of Birth" value={driver.dateofbirth} onChange={d('dateofbirth')} portalContainer={portalContainer} className="col-span-2" />
              : <HiddenField label="Date of Birth" className="col-span-2" />}
          </Section>
          <Section title="Contact">
            <TextField label="Home Phone" value={driver.homephone} onChange={d('homephone')} type="tel" />
            <TextField label="Cell / Text #" value={driver.textmsgnumber} onChange={d('textmsgnumber')} type="tel" />
            <TextField label="Email" value={driver.email} onChange={d('email')} type="email" className="col-span-2" />
            <TextField label="Emergency Contact" value={driver.emergencycontact} onChange={d('emergencycontact')} />
            <TextField label="Relationship" value={driver.emergencycontactrel} onChange={d('emergencycontactrel')} />
            <TextField label="Emergency Number" value={driver.emergencynumber} onChange={d('emergencynumber')} type="tel" className="col-span-2" />
          </Section>
        </div>

        {/* ---- Column 2: assignment / GWI Move / device / alerts / fuel card ---- */}
        <div className="space-y-3">
          <Section title="Assignment">
            <TextField label="Trainer" value={driver.trainer} onChange={d('trainer')} />
            <YesNoField label="Mule Driver" value={driver.muledriver} onChange={d('muledriver')} />
            <LookupSelectField label="Driver Type" value={driver.drivertype} onChange={d('drivertype')} options={lookupOptions(L.drivertypes)} />
            <LookupSelectField label="Region" value={driver.region} onChange={d('region')} options={lookupOptions(L.regions)} />
            <LookupSelectField label="Parking Location" value={driver.parkingloc} onChange={d('parkingloc')} options={lookupOptions(L.parkinglocs)} search />
            <SelectField label="Preference" value={driver.preference} onChange={d('preference')} options={PREFERENCE_OPTIONS} />
            <LookupSelectField label="Language" value={driver.language} onChange={d('language')} options={lookupOptions(L.languages)} />
            <TextField label="Experience" value={driver.experience} onChange={d('experience')} />
          </Section>
          <Section title="GWI Move">
            <YesNoField label="Use GWI Move" value={unit.usegwimove} onChange={u('usegwimove')} />
            <YesNoField label="Capture Geofence" value={unit.gwimove_capturegeofence} onChange={u('gwimove_capturegeofence')} />
            <YesNoField label="Auto Arrive" value={unit.gwimove_autoarrive} onChange={u('gwimove_autoarrive')} />
            <YesNoField label="Auto Depart" value={unit.gwimove_autodepart} onChange={u('gwimove_autodepart')} />
            <TextField label="Device Serial #" value={unit.deviceserialnum} onChange={u('deviceserialnum')} />
            <TextField label="Device Phone #" value={unit.devicephonenum} onChange={u('devicephonenum')} type="tel" />
          </Section>
          <Section title="Mobile / Alerts">
            <TextField label="Mobile PIN" value={driver.mobilepin} onChange={d('mobilepin')} className="col-span-2" />
            <YesNoField label="Dispatch Alert" value={driver.dispatchalert} onChange={d('dispatchalert')} />
            <YesNoField label="Message Alert" value={driver.messagealert} onChange={d('messagealert')} />
          </Section>
          <Section title="Fuel Card">
            <TextField label="Fuel Card #" value={driver.fuelcardnumber} onChange={d('fuelcardnumber')} />
            <TextField label="Fuel Type" value={driver.fueltype} onChange={d('fueltype')} />
            <TextField label="Fuel Amount" value={driver.fuelamount} onChange={d('fuelamount')} />
            <TextField label="Cash Amount" value={driver.cashamount} onChange={d('cashamount')} />
            <TextField label="Fuel Card Reg By" value={driver.fuelcardregby} onChange={d('fuelcardregby')} readOnly />
            <TextField label="Fuel Card Reg Date" value={str(driver.fuelcardregdatetime).replace('T', ' ').slice(0, 16)} onChange={() => {}} readOnly />
            <TextField label="UIIA Reg By" value={driver.uiiaregby} onChange={d('uiiaregby')} readOnly />
            <TextField label="UIIA Reg Date" value={str(driver.uiiaregdatetime).replace('T', ' ').slice(0, 16)} onChange={() => {}} readOnly />
          </Section>
        </div>

        {/* ---- Column 3: photo / status widget / dates ---- */}
        <div className="space-y-3">
          <div className="flex items-center justify-center rounded-md border border-rule bg-highlight p-2">
            {photoUrl ? (
              <img src={photoUrl} alt="Driver photo" className="max-h-48 rounded-md object-contain" />
            ) : (
              <div className="flex h-32 w-full items-center justify-center text-sm text-muted">No photo on file</div>
            )}
          </div>
          <Section title="Status">
            <SelectField label="Status" value={unit.status} onChange={u('status')} options={STATUS_OPTIONS} className="col-span-2" />
            {status === 'OUT OF SERVICE' && (
              <>
                <LookupSelectField label="Out of Service Type" value={unit.outofservicetype} onChange={u('outofservicetype')} options={lookupOptions(L.outofservicetypes)} />
                <DateField label="Out of Service Date" value={unit.outofservicedate} onChange={u('outofservicedate')} portalContainer={portalContainer} />
                <Field label="Out of Service Comments" className="col-span-2">
                  <PermissionAwareTextarea aria-label="Out of Service Comments" className={`${inputClass} min-h-[4rem]`} value={str(unit.outofservicecomments)}
                    onChange={(e) => onUnitChange('outofservicecomments', e.target.value)} />
                </Field>
              </>
            )}
            {status === 'TERMINATED' && (
              <>
                <DateField label="Terminate Date" value={unit.terminatedate} onChange={u('terminatedate')} portalContainer={portalContainer} />
                <LookupSelectField label="Terminate Reason" value={unit.terminatereason} onChange={u('terminatereason')} options={lookupOptions(L.terminatereasons)} />
                <SelectField label="Turnover" value={unit.terminateturnover} onChange={u('terminateturnover')} options={TURNOVER_OPTIONS} />
                <YesNoField label="Rehire" value={driver.rehire} onChange={d('rehire')} />
              </>
            )}
          </Section>
          <Section title="Dates">
            <DateField label="Contract Date" value={unit.contractdate} onChange={() => {}} portalContainer={portalContainer} readOnly />
            <DateField label="Driver Start Date" value={driver.driverstartdate} onChange={d('driverstartdate')} portalContainer={portalContainer} />
            <DateField label="Estimated Return Date" value={driver.estimatedreturndate} onChange={d('estimatedreturndate')} portalContainer={portalContainer} className="col-span-2" />
          </Section>
          <Section title="Referral">
            <TextField label="Referral" value={driver.referral} onChange={d('referral')} />
            <TextField label="Referral Other" value={driver.referralother} onChange={d('referralother')} />
          </Section>
          <Section title="GWI Move 2">
            <YesNoField label="Use GWI Move 2" value={unit.usegwimove2} onChange={u('usegwimove2')} className="col-span-2" />
          </Section>
        </div>
      </div>
    </PermissionAccessProvider>
  );
}
